import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Box, Autocomplete } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import {
  driverGroupSchema,
  updateDriverGroup,
} from "@lib/api/mutations/driver_groups";
import { DriverGroupFormData } from "@lib/api/mutations/driver_groups";
import { DriverGroup, DriverGroupResponse } from "@lib/types/driver_group";
import Loading from "@components/Loading";
import { Driver } from "@lib/types/driver";
import { getDrivers } from "@lib/api/queries/drivers";

interface Props {
  updateDriverGroupDialogOpen: boolean;
  setUpdateDriverGroupDialogOpen: (open: boolean) => void;
  driverGroupData?: DriverGroup;
  refetch?: () => void;
}

const UpdateDriverGroup: React.FC<Props> = ({
  updateDriverGroupDialogOpen,
  setUpdateDriverGroupDialogOpen,
  driverGroupData,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<DriverGroupFormData>({
    resolver: zodResolver(driverGroupSchema),
    defaultValues: {
      name: driverGroupData?.name || "",
      description: driverGroupData?.description || "",
      drivers:
        driverGroupData?.drivers.map((driverGroup) => driverGroup.driver_id) ||
        [],
    },
  });

  useEffect(() => {
    if (driverGroupData) {
      setValue("name", driverGroupData.name);
      setValue("description", driverGroupData.description);
      setValue(
        "drivers",
        driverGroupData.drivers.map((driver) => driver.driver_id)
      );
    }
  }, [driverGroupData, updateDriverGroupDialogOpen]);

  const { mutate, isPending: isUpdatingDriverGroup } = useMutation({
    mutationFn: updateDriverGroup,
    onSuccess: (data: DriverGroupResponse) => {
      showSnackbar(t("user.driverGroups.updateSuccess"), "success");
      setUpdateDriverGroupDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const {
    data: drivers,
    isFetching,
    refetch: refetchDrivers,
    error,
  } = useQuery<Driver[]>({
    queryKey: ["drivers"],
    queryFn: async () => {
      const drivers = await getDrivers();
      return drivers.data;
    },
    enabled: updateDriverGroupDialogOpen,
  });

  const handleUpdateDriverGroup = (data: DriverGroupFormData) => {
    if (!driverGroupData?.group_id) return;

    mutate({
      driverGroupId: driverGroupData.group_id,
      updateDriverGroupParams: data,
    });
  };

  return (
    <FormDialog
      title={t("user.driverGroups.update")}
      open={updateDriverGroupDialogOpen}
      onClose={() => {
        setUpdateDriverGroupDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleUpdateDriverGroup)}
      submitText={t("general.save")}
      isSubmitting={isUpdatingDriverGroup}
      submittingText={t("general.saving")}
    >
      {isFetching && <Loading />}

      {!isFetching && drivers && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.description")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            <Controller
              name="drivers"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field}
                    multiple
                    options={drivers || []}
                    value={
                      drivers?.filter((driver) =>
                        field.value?.includes(driver.driver_id)
                      ) || []
                    }
                    onChange={(_, value) => {
                      field.onChange(
                        value ? value.map((driver) => driver.driver_id) : []
                      );
                    }}
                    loading={false} // No need to fetch drivers here since data is passed as prop
                    isOptionEqualToValue={(option, value) =>
                      option.driver_id === value.driver_id
                    }
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.name}-${option.driver_id}`}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.bindings.driver")}
                        placeholder={t("user.bindings.selectDriver")}
                        variant="outlined"
                        error={!!errors.drivers}
                        helperText={errors.drivers?.message}
                      />
                    )}
                  />
                </div>
              )}
            />
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default UpdateDriverGroup;
